<template>
    <div v-if="can">
        <settings-heading
            :title="$t('settings.email-templates.title')"
            :description="$t('settings.email-templates.description_data_retention')"
        />

        <draggable-email-template
            v-model="retentionExtensionTemplates"
            disable-delete
            disable-duplicate
            disable-move
            :working="fetchingTemplates"
            class="mb-48"
            @edit="edit($event)"
            context="retention_extension"
        />


        <div class="flex items-end justify-between mb-32">
            <div class="flex-1">
                <h1 class="first-letter:uppercase text-heading-2 font-semibold mr-auto mb-16">
                    {{ $t('settings.email-templates.title_auto_reply') }}
                </h1>
                <p class="text-primary-80 mr-32">
                    {{ $t('settings.email-templates.description_auto_reply') }}
                </p>
            </div>
            <simple-button display="secondary" icon="add-circle" look="primary" @click="create('auto_reply')">
                {{ $t('settings.add_new', {subject: $t('settings.email-templates.template')}) }}
            </simple-button>
        </div>
        <draggable-email-template
            v-model="autoReplyTemplates"
            :working="fetchingTemplates"
            class="mb-48"
            @delete="confirmDelete($event)"
            @duplicate="duplicate($event)"
            @edit="edit($event)"
            context="auto_reply"
        ></draggable-email-template>


        <div class="flex items-end justify-between mb-32">
            <div class="flex-1">
                <h1 class="first-letter:uppercase text-heading-2 font-semibold mr-auto mb-16">
                    {{ $t('settings.email-templates.title_general') }}
                </h1>
                <p class="text-primary-80 mr-32">
                    {{ $t('settings.email-templates.description_general') }}
                </p>
            </div>
            <simple-button display="secondary" icon="add-circle" look="primary" @click="create('general')">
                {{ $t('settings.add_new', {subject: $t('settings.email-templates.template')}) }}
            </simple-button>
        </div>

        <draggable-email-template
            v-model="generalTemplates"
            :use-empty-state="true"
            :working="fetchingTemplates"
            class="mb-48"
            @create="create('general')"
            @delete="confirmDelete($event)"
            @duplicate="duplicate($event)"
            @edit="edit($event)"
            context="general"
        ></draggable-email-template>


        <div class="flex items-end justify-between mb-32">
            <div class="flex-1">
                <h1 class="first-letter:uppercase text-heading-2 font-semibold mr-auto mb-16">
                    {{ $t('settings.email-templates.title_interviews') }}
                </h1>
                <p class="text-primary-80 mr-32">
                    {{ $t('settings.email-templates.description_interviews') }}
                </p>
            </div>
            <simple-button display="secondary" icon="add-circle" look="primary" @click="create('interview')">
                {{ $t('settings.add_new', {subject: $t('settings.email-templates.template')}) }}
            </simple-button>
        </div>

        <draggable-email-template
            v-model="interviewTemplates"
            :use-empty-state="true"
            :working="fetchingTemplates"
            class="mb-32"
            @create="create('interview')"
            @delete="confirmDelete($event)"
            @duplicate="duplicate($event)"
            @edit="edit($event)"
            context="interview"
        ></draggable-email-template>

        <div class="flex items-end justify-between mb-32">
            <div class="flex-1">
                <h1 class="first-letter:uppercase text-heading-2 font-semibold mr-auto mb-16">
                    {{ $t('settings.email-templates.title_interim') }}
                </h1>
                <p class="text-primary-80 mr-32">
                    {{ $t('settings.email-templates.description_interim') }}
                </p>
            </div>
            <simple-button display="secondary" icon="add-circle" look="primary" @click="create('interim')">
                {{ $t('settings.add_new', {subject: $t('settings.email-templates.template')}) }}
            </simple-button>
        </div>

        <draggable-email-template
            v-model="interimTemplates"
            :use-empty-state="true"
            :working="fetchingTemplates"
            class="mb-32"
            @create="create('interim')"
            @delete="confirmDelete($event)"
            @duplicate="duplicate($event)"
            @edit="edit($event)"
            context="interim"
        ></draggable-email-template>

        <template-editor
            v-if="editing"
            :template="editing"
            :type="editing.type"
            @close="stopEdit()"
        />

        <template-editor
            v-if="creating"
            :auto-reply="creating === 'auto_reply'"
            :type="creating"
            @close="stopCreate()"
        />

        <popup-modal-confirm
            v-if="deleting"
            :confirm-text="$t('general.delete_subject', {subject: deleting.name[active_locale]})"
            :message="$t('general.confirm_delete_subject', {subject: deleting.name[active_locale]})"
            show-exclamation
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="deleteTemplate(deleting)"
        />

        <popup-modal-confirm
            v-if="duplicating"
            :confirmText="$t('general.duplicate')"
            :message="$t('settings.email-templates.duplicate.description')"
            :title="$t('settings.email-templates.duplicate.title')"
            :working="performingDuplicateRequest"
            confirm-button="success"
            @close="cancelDuplicate"
            @confirm="duplicateTemplate(duplicating)"
        />
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import SimpleButton from '../../components/ui/SimpleButton.vue';
import * as templates from '../../services/EmailTemplateService';
import EmailTemplate from '../../models/EmailTemplate';
import {normalizeJson} from '../../../shared/helpers';
import {mapState, mapActions} from 'pinia';
import {mapRepos} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import EmailTemplateEditor from '../../components/ui/EmailTemplateEditor.vue';
import Error403 from '../../components/ui/Error403.vue';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import DraggableEmailTemplate from '../../components/DraggableEmailTemplate';
import SettingsHeading from "../../components/ui/SettingsHeading.vue";

export default {
    components: {
        SettingsHeading,
        'draggable-email-template': DraggableEmailTemplate,
        'simple-button': SimpleButton,
        'popup-modal-confirm': PopupModalConfirm,
        'template-editor': EmailTemplateEditor,
        'error-403': Error403,
    },
    data: function () {
        return {
            fetchingTemplates: true,
            editing: null,
            creating: false,
            deleting: null,
            duplicating: null,
            performingDeleteRequest: false,
            performingDuplicateRequest: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        ...mapRepos({emailTemplateRepo: EmailTemplate}),
        active_locale() {
            return this.currentCompany.jobsite.active_languages.includes(this.locale) ? this.locale : this.currentCompany.jobsite.active_languages[0];
        },
        templateCount() {
            return this.emailTemplateRepo.all().length;
        },
        generalTemplates: {
            get() {
                return this.emailTemplateRepo
                    .with('attachments')
                    .where('type', 'general')
                    .orderBy('order')
                    .get();
            },
            set(sortedTemplates) {
                let sort = sortedTemplates.map((template, index) => ({id: template.id, order: index}));

                this.emailTemplateRepo.save(sort);
            },
        },
        autoReplyTemplates: {
            get() {
                return this.emailTemplateRepo
                    .with('attachments')
                    .where('type', 'auto_reply')
                    .orderBy('order')
                    .get();
            },
            set(sortedTemplates) {
                let sort = sortedTemplates.map((template, index) => ({id: template.id, order: index}));

                this.emailTemplateRepo.save(sort);
            },
        },
        retentionExtensionTemplates: {
            get() {
                return this.emailTemplateRepo
                    .with('attachments')
                    .where('type', 'retention_extension')
                    .orderBy('order')
                    .get();
            },
            set(sortedTemplates) {
                let sort = sortedTemplates.map((template, index) => ({id: template.id, order: index}));

                this.emailTemplateRepo.save(sort);
            },
        },
        interviewTemplates: {
            get() {
                return this.emailTemplateRepo
                    .with('attachments')
                    .where('type', 'interview')
                    .orderBy('order')
                    .get();
            },
            set(sortedTemplates) {
                let sort = sortedTemplates.map((template, index) => ({id: template.id, order: index}));

                this.emailTemplateRepo.save(sort);
            },
        },
        interimTemplates: {
            get() {
                return this.emailTemplateRepo
                    .with('attachments')
                    .where('type', 'interim')
                    .orderBy('order')
                    .get();
            },
            set(sortedTemplates) {
                let sort = sortedTemplates.map((template, index) => ({id: template.id, order: index}));

                this.emailTemplateRepo.save(sort);
            },
        },
        can() {
            return this.$can('settings.applications');
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        fetchTemplates() {
            return templates.index().then(result => {
                return this.emailTemplateRepo.save(normalizeJson(result.data));
            });
        },
        create(type) {
            this.creating = type;
        },
        stopCreate() {
            this.creating = false;
        },
        duplicate(template) {
            this.duplicating = template;
        },
        edit(template) {
            this.editing = template;
        },
        stopEdit() {
            this.editing = null;
        },
        confirmDelete(template) {
            this.deleting = template;
        },
        cancelDelete() {
            this.deleting = null;
        },
        cancelDuplicate() {
            this.duplicating = null;
        },
        deleteTemplate(template) {
            this.performingDeleteRequest = true;

            templates.destroy(template.id).then(() => {

                if (template.core_template) {

                    return templates.show(this.currentCompany.id + '-' + template.core_template)
                        .then(result => {
                            this.emailTemplateRepo.destroy(template.id);
                            this.emailTemplateRepo.save(normalizeJson(result.data));
                        });

                } else {
                    return this.emailTemplateRepo.destroy(template.id);
                }
            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            });
        },
        duplicateTemplate(template) {
            this.performingDuplicateRequest = true;

            templates.duplicate(template.id).then(result => {
                this.emailTemplateRepo.save(normalizeJson(result.data));
            }).finally(() => {
                this.duplicating = null;
                this.performingDuplicateRequest = false;
            });
        },
    },
    mounted() {
        this.fetchingTemplates = true;

        this.fetchTemplates().then(inserts => {
            this.fetchingTemplates = false;
        });
    },
};
</script>
